import { EventEmitter, Injectable } from '@angular/core';
import {
    DialerConfig, DialerConfigService, DialerService, DialerTestService, RootTest, RootTestResult,
    DialerTestSpecContext, CallSetProfile, RootTestContext, OfonoPhone, VoiceCallTest, DialerTest, isFinishedTest, DialerTestStatus, NetworkStatusEvent,
    GroupTest,
    CallSetProfileValidationRule
} from 'dialer';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, of, race, Subscription, timer } from 'rxjs';
import { AppState } from '../app-state';
import { PathTestPoint, PathTestPointSeq, activity_status, TestSeqStatus } from '../models';
import { PointTestLogService } from './point-test-log.service';
import { PathTestService } from './path-test.service';
import { TestSeqService } from './test-seq.service';
import { catchError, distinctUntilKeyChanged, filter, map, skip, skipWhile, switchMap, take } from 'rxjs/operators';
import { PathTestDoc } from '../schema/path-test-schema';
import  '../collection-utils'
import { diff } from 'deep-object-diff';
import { LookupService } from './lookup.service';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from '../../../environments/environment';
import { DriveTestSeqService } from './drive-test-seq.service';
import {DriveTestDoc} from "../schema/drive-test-schema";
import {DriveTestService} from "./drive-test.service";
import { TestSeqDoc } from '../schema/test-seq-schema';
import { DriveTestSeqDoc } from '../schema/drive-test-seq-schema';
import { TestLog } from 'dialer/lib/core/model/test-log';


//TODO - this needs to be revisited once dialer returns routeId for drive tests.
//       Also, it should have fall back if the rootTest is missing both pointId AND routeId
const isDriveTest = ( rootTest: RootTest ) => !rootTest.pointId


export const durationToText = (durationSecs: number) => {
    let msg = ''
    if (durationSecs > 60 * 60) {
        msg += `${(durationSecs / (60 * 60.0)).toFixed(3)} hours`
    } else if (durationSecs > 60) {
        msg += `${(durationSecs / (60.0)).toFixed(3)} minutes`
    } else if ( durationSecs != null ) {
        msg += `${durationSecs.toFixed(3)} seconds`
    }
    return msg
}

export const rootTestsEqual = (t1:RootTest,t2:RootTest) => Object.keys(diff(t1,t2)).length === 0;

@Injectable({ providedIn: 'root' })
export class DialerClientService {

    rootTestSubscription: Subscription
    dialerInitializedSubscription: Subscription
    currentDialerTest : RootTest
    rootTestProcessingError$ = new EventEmitter<{rootTest:RootTest,errorMsg:string}>()
    syncUnimportedSubscription : Subscription
    networkStatusSubscription : Subscription

    constructor(
        public appState: AppState,
        public dialerService: DialerService,
        public dialerConfigService: DialerConfigService,
        public dialerTestService: DialerTestService /*from dialer module to communicate with PI box*/,
        public testSeqService: TestSeqService,
        public _driveTestSeqService: DriveTestSeqService,
        public pathTestService: PathTestService,
        public pointTestLogService: PointTestLogService,
        public driveTestService: DriveTestService,
        public lookupService: LookupService,
        public httpClient: HttpClient ) {
            this.dialerConfigService.profileValidationRules = AppConfig.dialer?.profileValidationRules;
            this.dialerConfigService.allowUserEdits = AppConfig.dialer?.allowUserProfileEdits??true;
            this.appState.doWhenReady( () => this.initDialerConfig() );
    }

    async initDialerConfig() {
        const dialerCfg = await this.lookupService.rxCollection.findOne('dialer').exec();
        const dialerProfile = dialerCfg?.values.find(section=>section.id === 'profile');
        if ( dialerProfile ) {
            if ( dialerProfile.validationRules != null ) {
                this.dialerConfigService.profileValidationRules = dialerProfile.validationRules;
            }
            if ( dialerProfile.allowUserEdits != null ) {
                this.dialerConfigService.allowUserEdits = dialerProfile.allowUserEdits;
            }
        }
    }

    profileValidationRulesSubscription: Subscription;
    profileValidationRulesChanged$ = new BehaviorSubject<CallSetProfileValidationRule[]>([]);

    protected listenForRemoteProfileValidationRuleChanges() {
        this.profileValidationRulesSubscription?.unsubscribe();
        this.profileValidationRulesSubscription = this.lookupService.rxCollection.findOne('dialer').$.pipe(skip(1)).subscribe( dialerConfig => {
            console.log('LOOKUP change detected for dialerConfig', dialerConfig?.toJSON());
            const profile = dialerConfig?.values.find(section=>section.id === 'profile');
            const dialerConfigService = this.dialerConfigService;
            if ( profile?.allowUserEdits != null ) {
                dialerConfigService.allowUserEdits = profile?.allowUserEdits;
            }
            if ( profile?.validationRules != null ) {
                dialerConfigService.profileValidationRules = profile?.validationRules;
                this.profileValidationRulesChanged$.next(dialerConfigService.profileValidationRules);
            }
        });
    }

    enableDialer(bEnable = true) {
        console.log(`enableDialer(${bEnable})`)
        if (bEnable) {
            this.listenForRemoteProfileValidationRuleChanges();
            this.dialerConfigService.defaultProfile =
                Object.assign(new CallSetProfile(), AppConfig.dialer?.defaultProfile)

            // this.dialerConfigService.profileValidationRules =
            //     [...AppConfig.dialer?.profileValidationRules];

            if ( AppConfig.dialer?.earlyDisconnectToleranceSecs != null ) {
                CallSetProfile.earlyDisconnectToleranceSecs = AppConfig.dialer.earlyDisconnectToleranceSecs
            }
            if ( AppConfig.dialer?.earlyDisconnectCarrierRegex != null ) {
                CallSetProfile.earlyDisconnectCarrierRegex = AppConfig.dialer.earlyDisconnectCarrierRegex
            }

            const dialerServiceConfig = {
                // For https testbed clients, the dialer url must also use https
                // The exception is http://127.0.0.1 and ws://127.0.0.1

                // Add dialer:8000 as 127.0.0.1:8001
                //C:\>netsh interface portproxy add v4tov4 listenaddress=127.0.0.1 listenport=8001 connectaddress=dialer connectport=8000
                // Remove dialer rule later...
                //C:\>netsh interface portproxy delete v4tov4 listenaddress=127.0.0.1 listenport=8001

                url: this.appState.settings.getDialerGraphQLUrl(),
                ws: this.appState.settings.getDialerGraphQLWsUrl()
            }
            this.dialerService.initialize(dialerServiceConfig)

            try {
                this.dialerConfigService.load()
            } catch (error) {
                console.error('Failed loading dialer configuration from local storage')
                this.dialerConfigService.save(this.dialerConfigService.defaultConfig)
            }
            try {
                this.dialerConfigService.loadProfile()
            } catch (error) {
                console.error('Failed loading dialer profile from local storage')
                this.dialerConfigService.saveProfile(this.dialerConfigService.defaultProfile)
            }

            const enableRootTestSubscription = (bEnable:boolean=true) => {
                console.log(`enableRootTestSubscription(${bEnable})`)
                if ( bEnable ) {
                    if ( this.rootTestSubscription ) {
                        return
                    }
                    this.rootTestSubscription = merge(
                        this.dialerTestService.createFetchRootTest$(),
                        this.dialerTestService.rootTest$
                    ).subscribe( {
                        next: this.handleRootTestChange,
                        error: (e) => {
                            console.error(e);
                            this.appState.notifyError(e?.message||'Failed dialer listener initialization',this,3);
                            this.appState.publishError(e,this);
                        },
                    })
                } else {
                    this.rootTestSubscription?.unsubscribe()
                    this.rootTestSubscription = null
                }
            }
            this.syncUnimportedSubscription?.unsubscribe();
            const networkStatus$ = this.dialerService.networkStatus$.pipe(
                skipWhile(status=>status.eventType==NetworkStatusEvent.Undefined),
                distinctUntilKeyChanged('ok')
            );
            this.syncUnimportedSubscription = combineLatest([
                this.dialerService.initialized$.pipe(filter(bInit=>bInit)),
                networkStatus$.pipe(filter(status=>status.ok))
            ]).pipe(take(1)).subscribe( async _ => this.syncUnimportedResultsFromPi());
            this.networkStatusSubscription?.unsubscribe()
            this.networkStatusSubscription = networkStatus$.subscribe( async status => {
                const dialerTest = this.currentDialerTest;
                // keep this section wrapped in processing root change to prevent
                // wrong order when enabling/disabling root test subscription
                await this.beginProcessingRootChange()
                if ( !status.ok ) {
                    if ( dialerTest ) {
                        if ( !['PAUSED','PAUSING'].includes(dialerTest.status)
                                && dialerTest.startTime && !isFinishedTest(dialerTest) ) {
                            try {
                                await this.saveUnfinishedTestSeqDocForRootTest(dialerTest)
                            } catch ( error: any ) {
                                console.error(error)
                                this.appState.publishError(error)
                            } finally {
                            }
                        }
                    }
                    enableRootTestSubscription(false)
                } else {
                    enableRootTestSubscription(true)
                }
                this.endProcessingRootChange()
            })
        } else {
            this.dialerService.destroy()
            this.rootTestSubscription?.unsubscribe()
            this.rootTestSubscription = null
            this.dialerInitializedSubscription?.unsubscribe()
            this.syncUnimportedSubscription?.unsubscribe()
            this.profileValidationRulesSubscription?.unsubscribe()
        }
    }

    lastCompleteRootTest : RootTest

    getTestDocByTestPointIdAndType = async ( testPointId: string, testType: 'path'|'drive' ) => testType=='drive' ?
        this.getDriveTestDocByTestPointId(testPointId) : this.getPathTestDocByTestPointId(testPointId);

    getPathTestDocByTestPointId = async ( testPointId: string) : Promise<PathTestDoc> => {
        const testDocs = await this.pathTestService.rxCollection.find().where('testPoints').elemMatch({id: {$eq: testPointId}}).exec()
        if (testDocs.length > 1) {
            throw new Error(`Multiple path tests found containing a test point ID: '${testPointId}' pathDocs: ${testDocs.map(pt => pt._id)}`)
        }
        return testDocs[0];
    }

    getDriveTestDocByTestPointId = async ( testPointId: string ) : Promise<DriveTestDoc> => {
        const testDocs = await this.driveTestService.rxCollection.find().where('testPoints').elemMatch({id: {$eq: testPointId}}).exec()
        if (testDocs.length > 1) {
            throw new Error(`Multiple drive tests found containing a test point ID: '${testPointId}' pathDocs: ${testDocs.map(pt => pt._id)}`)
        }
        return testDocs[0];
    }

    isDuplicateDialerTest = (rootTest: RootTest) => {

        const lastDialerTest = this.currentDialerTest
        let bDuplicate = true

        if ( !rootTest || rootTest.id != lastDialerTest?.id ) {
            //console.log(`ID changed from ${lastDialerTest?.id} to ${rootTest?.id}`)
            bDuplicate = false
        } else if ( rootTest.status != lastDialerTest?.status) {
            //console.log(`Status changed from ${lastDialerTest?.status} to ${rootTest.status}`)
            bDuplicate = false
        } else if ( rootTest.testDuration != lastDialerTest?.testDuration ) {
            //console.log(`Test duration changed from ${lastDialerTest?.testDuration} to ${rootTest.testDuration}`)
            bDuplicate = false
        } else {
            const diffObj = diff(lastDialerTest,rootTest)
            if ( Object.keys(diffObj).length ) {
                //console.log(`Last dialer test object different:`)
                console.dir(diffObj)
                bDuplicate = false
            }
        }
        return bDuplicate
    }

    processingRootChange$ = new BehaviorSubject<boolean>(false)

    beginProcessingRootChange = async () => {
        if ( this.processingRootChange$.value ) {
            console.warn("handleRootTestChange() *** Waiting for previous rootTest to be processed ***")
            await firstValueFrom(this.processingRootChange$.pipe(filter(bProcessing=>!bProcessing)))
        }
        this.processingRootChange$.next(true)
    }

    endProcessingRootChange = () => {
        this.processingRootChange$.next(false)
    }

    saveUnfinishedTestSeqDocForRootTest = async (rootTest: RootTest) => {
        const seqId = rootTest?.seqId;
        if ( !seqId ) {
            return
        }
        console.log(`saveUnfinishedTestSeqDocForRootTest(${rootTest?.id},seqId=${seqId})`,rootTest)
        try{
            const bDriveTest = isDriveTest(rootTest)
            const testSeqDoc = bDriveTest ?
                    await this._driveTestSeqService.rxCollection.findOne(rootTest.seqId).exec()
                    : await this.testSeqService.rxCollection.findOne(rootTest.seqId).exec()
            if ( testSeqDoc ) {
                if ( testSeqDoc.status == rootTest.status
                    && testSeqDoc.startTime == rootTest.startTime
                    && rootTestsEqual(testSeqDoc.dialer,rootTest) ) {
                    console.warn('Unfinished rootTest already saved for testSeq',testSeqDoc.toJSON())
                    return;
                }
                const updatedDoc = await (testSeqDoc as any).atomicUpdate( (d:TestSeqDoc|DriveTestSeqDoc) => {
                    d.dialer = rootTest;
                    d.status = rootTest.status;
                    d.userId = this.appState.getUser()?._id;
                    d.startTime = rootTest.startTime;
                    if ( !d.endTime ) {
                        let testStartDate = new Date(rootTest.testStartTime)
                        let testStopDate = new Date(testStartDate.setSeconds(testStartDate.getSeconds() + rootTest.testDuration)).toISOString()
                        d.endTime = testStopDate
                    }
                    return d;
                });
                console.log('saveUnfinishedTestSeqDocForRootTest() updated existing doc.',updatedDoc.toJSON())
                const testDoc = await this.getTestDocByTestPointIdAndType(rootTest.testPointId,bDriveTest?'drive':'path');
                if ( testDoc ) {
                    const testPoint = testDoc.testPoints?.find(tp=>tp.id==rootTest.testPointId)
                    const seq = testPoint.seqs?.find(s=>s.dialerSeqId==rootTest.seqId)
                    await this.pointTestLogService.logComment(seq.id,`[Saved] Unfinished dialer test results saved with status ${rootTest?.status}.`)
                }
            }
        } catch ( error: any ) {
            console.error(error)
            this.appState.publishError(error);
        } finally {
            console.log('saveUnfinishedTestSeqDocForRootTest() complete.')
        }
    }

    handleRootTestChange = async ( testResult: RootTestResult ) => {

        if ( testResult.errors?.length ) {
            const errorMsg = testResult.errors.join(', ')
            console.error('handleRootTestChange() RootTestResult errors: ', errorMsg)
            this.appState.publishError(errorMsg)
            this.rootTestProcessingError$.emit({rootTest:testResult?.test,errorMsg})
            return
        }

        const rootTest: RootTest = testResult.test

        await this.beginProcessingRootChange()

        try {
        if ( this.currentDialerTest ) {
            if ( !rootTest || rootTest.id !== this.currentDialerTest.id) {
                if ( !['PAUSED','PAUSING'].includes(this.currentDialerTest.status)
                        && !isFinishedTest(this.currentDialerTest)) {
                    await this.saveUnfinishedTestSeqDocForRootTest(this.currentDialerTest)
                }
            }
        }

        if ( !rootTest ) {
            console.warn( `handleRootTestChange() NULL rootTest!!!!`)
            return
        }

        console.log( `handleRootTestChange() ${rootTest.name}: ${rootTest.status}, testPointId: ${rootTest.testPointId}, seqId: ${rootTest.seqId}`)

        const isDuplicate = this.isDuplicateDialerTest(rootTest)
        this.currentDialerTest = rootTest

        if ( isDuplicate ) {
            console.log('handleRootTestChange() Ignoring duplicate test (not updating PathTestPoint or comment log)')
            return
        }

        if ( !isFinishedTest(rootTest) ) {
            if (rootTest.status == 'PAUSED' && rootTest.testPointId) {
                console.log('Saving paused work');
                await this.saveUnfinishedTestSeqDocForRootTest(rootTest);
            }
            console.log('handleRootTestChange() ignoring rootTest with status', rootTest.status)
            return
        }

        this.lastCompleteRootTest = rootTest

        var testPoint : PathTestPoint = null
        const endTime = new Date().toISOString();

        if ( !rootTest.testPointId ) {
            console.log('handleRootTestChange() No testPointId for result test (not updating PathTest testPoint with dialer result seqId)')
            return
        }

        const bDriveTest = isDriveTest(rootTest)
        const pathTestDoc = await this.getTestDocByTestPointIdAndType(rootTest.testPointId,bDriveTest?'drive':'path')

        if ( !pathTestDoc ) {
            console.warn(`handleRootTestChange() No rxdb path test doc found where testPoint.id=${rootTest.testPointId} (could be from another environment)`)
            return
        }
        const testService = bDriveTest ? this.driveTestService : this.pathTestService

        if ( !rootTest.seqId ) {
            const errorMsg = 'Cannot process dialer test results without a sequence ID'
            console.error(errorMsg)
            console.dir(rootTest)
            this.rootTestProcessingError$.emit({rootTest,errorMsg})
        } else try {
            testPoint = pathTestDoc.testPoints?.find(tp=>tp.id==rootTest.testPointId)
            const testSeqDoc = bDriveTest ?
                await this._driveTestSeqService.rxCollection.findOne(rootTest.seqId).exec()
                : await this.testSeqService.rxCollection.findOne(rootTest.seqId).exec()
            if ( testSeqDoc ) {
                let testChanged = true
                if ( testSeqDoc.endTime ) {
                    const diffObj = diff(testSeqDoc.dialer,rootTest)
                    testChanged = Object.keys(diffObj).length > 0
                    if ( testChanged ) {
                        console.warn(`handleRootTestChange() - testSeqDoc already has endTime but rootTest has differences.`)
                        console.dir(diffObj)
                    } else {
                        console.warn(`handleRootTestChange() - testSeqDoc.dialer == rootTest so not updating.`)
                    }
                }
                if ( testChanged ) {
                    await (testSeqDoc as any).atomicUpdate( (d:TestSeqDoc|DriveTestSeqDoc) => {
                        console.log(`handleRootTestChange() *** Root test changed - updating testSeq (${d?.status})***`)
                        console.log(testPoint)
                        d.dialer = rootTest;
                        d.status = TestSeqStatus.DIALER_SEQ_FINISHED;
                        d.userId = this.appState.getUser()?._id,
                        d.endTime = testPoint.seqs.find(seq=>seq.id==rootTest.seqId)?.stopTime
                        if ( !d.endTime ) {
                            let testStartDate = new Date(rootTest.testStartTime)
                            let testStopDate = new Date(testStartDate.setSeconds(testStartDate.getSeconds() + rootTest.testDuration)).toISOString()
                            d.endTime = testStopDate
                        }
                        return d
                    })
                }
            } else {
                console.log("No rxdb dialer results found by sequence ID: " + rootTest.seqId)
            }
        } catch ( error ) {
            const errorMsg = 'Failed searching for or updating rxdb dialer result doc. ' + error?.message
            console.error(errorMsg)
            console.error(error)
            this.testSeqService.publishError(errorMsg,this)
            this.rootTestProcessingError$.emit({rootTest,errorMsg})
            console.error(['name','status','testPointId','seqId'].map(key=>`${key}=${rootTest[key]}`))
        }

        console.log(`Path test: ${pathTestDoc?._id}, testPoint.pointId: ${testPoint?.pointId}, ` +
            `testPoint.id: ${testPoint?.id}, testPoint.dialerSequenceId: ${testPoint?.seqs?.last()?.dialerSeqId}, ` +
            `testPoint.statusId: ${activity_status.Names.byId(testPoint.statusId)}`)
        if ( testPoint ) {
            const seq = testPoint.seqs?.find(s=>s.dialerSeqId==rootTest.seqId)
            if ( !seq ) {
                const msg = `Could not find test point sequence for a dialer result with seqId=${rootTest.seqId}`;
                console.warn(`handleRootTestChange() ${msg}.`)
                this.pointTestLogService.publishError(msg,this)
            } else if ( seq.startTime && !seq.stopTime ) {
                console.log(`handleRootTestChange() Stopping test point seq test started at ${seq?.startTime}.`)
                try {
                    const seq = testPoint?.seqs.find(s=>s.dialerSeqId==rootTest.seqId)
                    const rootTestContext = new RootTestContext(rootTest)
                    const [callsPlaced,callsCompleted] = this.extractCallCounts(rootTestContext)
                    const updatedTestPoint = await testService.stopTest(pathTestDoc,testPoint,seq,{
                        callsPlaced,
                        callsCompleted
                    })
                    if ( updatedTestPoint ) {
                        const strTestPointStatus = await this.pointTestLogService.textForStatusId(updatedTestPoint.statusId)
                        if ( seq ) {
                            await this.pointTestLogService.logComment(seq.id,strTestPointStatus)
                        }
                    } else {
                        console.error('Unexpected state.  Expected updated test point after stopping test')
                    }
                } catch ( error ) {
                    const errorMsg = `Error occured stopping test (pathTestDoc:${pathTestDoc?._id},testPoint:${testPoint?.id})`
                    console.error(errorMsg)
                    console.error(error)
                    this.testSeqService.publishError(errorMsg,this)
                    //this.appState.notifyError(errorMsg,this,3)
                    this.rootTestProcessingError$.emit({rootTest,errorMsg})
                }
            } else {
                if ( seq.stopTime ) {
                    console.log(`handleRootTestChange() testPoint seq already stopped (not updating testPoint or comment log).`)
                } else {
                    console.error(`handleRootTestChange() Invalid state (missing startTime).`)
                }
            }
            const ptSeqId = testPoint.seqs?.last()?.dialerSeqId
            if ( ptSeqId != rootTest.seqId ) {
                console.log(`handleRootTestChange() Updating pathTestDoc testPoint with new seq ${rootTest.seqId} (old=${ptSeqId})`)
                try{
                    await testService.updateTestPoint(pathTestDoc,testPoint, (tp)=>{
                        if ( !tp.seqs ) {
                            tp.seqs = []
                        }
                        var seq = tp.seqs.find(seq=>seq.dialerSeqId == ptSeqId )
                        if ( !seq ) {
                            seq = new PathTestPointSeq()
                            seq.dialerSeqId = ptSeqId
                            tp.seqs.push(seq)
                        }
                        if ( !seq.data ) {
                            seq.data = {}
                        }
                        const rootTestContext = new RootTestContext(rootTest)
                        const [callsPlaced,callsCompleted] = this.extractCallCounts(rootTestContext)
                        seq.data.callsPlaced = callsPlaced
                        seq.data.callsCompleted = callsCompleted
                        seq.stopTime = endTime
                        console.log('Added new seq:')
                        console.dir(seq)
                    })
                } catch ( error ) {
                    const errorMsg = 'Failed updating path test task doc after dialer stopped. ' + error?.message
                    console.error(errorMsg)
                    testService.publishErrorObj(error,errorMsg,null)
                    //this.appState.notifyError(errorMsg,this,5)
                    this.rootTestProcessingError$.emit({rootTest,errorMsg})
                }
            } else {
                console.log(`handleRootTestChange() pathTestDoc testPoint already has sequenceId ${rootTest.seqId}`)
            }
        } else if ( rootTest.testPointId ) {
            const errorMsg = 'Could not find path task testPoint specified by dialer result'
            console.error(errorMsg)
            testService.publishError(errorMsg,this)
            this.rootTestProcessingError$.emit({rootTest,errorMsg})
        }
        } finally {
            this.endProcessingRootChange()
        }
    }

    extractCallCounts( rootTestContext: RootTestContext ) {
        const voiceCallTests = this.dialerTestService.flattenTest(rootTestContext.rootTest).filter(t=>t.type=='VoiceCallTest')
        return [voiceCallTests.filter(t=>t.call).length,voiceCallTests.filter(t=>rootTestContext.reachedExpectedDuration(t)).length]
    }

    extractPhones( rootTest: RootTest ) {
        const phoneMap = {};
        this.dialerTestService.flattenTest(rootTest).filter((t:DialerTest)=>t.type=='VoiceCallTest')
            .forEach( (t:VoiceCallTest) =>{
                // voiceCallTests phone.mac don't have mac (at this time).
                const mac = t.phoneMacAddress || t.phone.mac
                phoneMap[mac] = { ...t.phone, mac }
            });
        return Object.values(phoneMap) as OfonoPhone[];
    }

    rootTestToSeqDocStatus( rootTestStatus: string ) {
        return ['PASSED','FAILED'].includes(rootTestStatus) ? 'COMPLETE' : rootTestStatus
    }


    async runSeq(args:DialerTestSpecContext, dialerConfig: DialerConfig) : Promise<RootTest> {
        const profile = this.dialerConfigService.defaultProfile
        const dialerConfigTestSpec = this.dialerConfigService.buildTestSpec(args, dialerConfig,profile)
        const rootTest = await this.dialerTestService.runTestSpec(dialerConfigTestSpec);
        if ( args.testPointId) {
            let testSeqDoc;
            if(args.routeId)   {
                 testSeqDoc = this._driveTestSeqService.rxCollection.newDocument({
                    seqid: rootTest.seqId,
                    routeId: args.routeId,
                    testPointId: args.testPointId,
                    importTime: null,
                    dialer: null,
                    cycle: args.cycle,
                    stage: args.stage,
                    startTime: args.startTime,
                    endTime: null,
                    testOrder: args.testOrder,
                    userId: args.userId,
                     groupingfield:args.groupingfield||null
                    // updatedAt: new Date().getUTCMilliseconds(),
                    // _rev: new Date().getUTCMilliseconds() + 'graphqlrevision'
                })
            }  else {
                 testSeqDoc = this.testSeqService.rxCollection.newDocument({
                    seqid: rootTest.seqId,
                    pointId: args.pointId,
                    testPointId: args.testPointId,
                    pathId: args.pathId,
                    importTime: null,
                    dialer: null,
                    buildingId: args.buildingId,
                    cycle: args.cycle,
                    stage: args.stage,
                    startTime: args.startTime,
                    endTime: null,
                    testOrder: args.testOrder,
                    userId: args.userId,
                     groupingfield:args.groupingfield||null
                    // updatedAt: new Date().getUTCMilliseconds(),
                    // _rev: new Date().getUTCMilliseconds() + 'graphqlrevision'
                })
            }

            console.log('runSeq() ', testSeqDoc.toJSON())
            try {
                await testSeqDoc.save()
            } catch ( error ) {
                this.appState.publishError(error,this)
            }
        } else {
            console.log('Unlogged dialer test run... not saving for testbed server sync')
        }
        return rootTest
    }


    async stopTest(dialerTestId: string) {
        console.log(`stopTest(${dialerTestId})`);
        const testStatus = await this.dialerTestService.stopTest(dialerTestId);
        return testStatus;
    }

    async pauseTest(dialerTestId: string) : Promise<DialerTestStatus> {
        console.log(`pauseTest(${dialerTestId})`);
        const testStatus = await this.dialerTestService.pauseTest(dialerTestId);
        return testStatus;
    }

    async resumeTest(dialerTestId: string) : Promise<DialerTestStatus> {
        console.log(`resumeTest(${dialerTestId})`);
        const testStatus = await this.dialerTestService.resumeTest(dialerTestId);
        return testStatus;
    }

    async reviveTest(dialerTest: RootTest) : Promise<RootTest> {
        console.log(`reviveTest()`,dialerTest);
        const rootTest = await this.dialerTestService.reviveTest(dialerTest);
        //console.dir(rootTest)
        return rootTest;
    }

    async syncUnimportedResultsFromPi() {
        console.log('syncUnimportedResultsFromPi()')
        try {
            const dialerTestLogs: Partial<TestLog>[] = await this.dialerTestService.findPiTestLogsWhere({
                imported: false,
                env: AppConfig.environment
            }, ['id','pointId','testPointId','seqId','type','startTime'])
            //console.log(dialerTestLogs)
            // TBD - what if adding something keeps failing? i.e. old rxdb schema format
            for ( const testLog of dialerTestLogs )  {
                try {
                    const pathTestTask = await this.getTestDocByTestPointIdAndType(testLog.testPointId, testLog.type)

                    if ( !pathTestTask ) {
                        console.warn('Test result on dialer box with no importTime could not found by testPointId in this environment (ignoring)')
                        console.warn(`Dialer result: startTime=${testLog.startTime}, pointId=${testLog.pointId}, seqId=${testLog.seqId}`)
                        continue;
                    }

                    const bDriveTest = testLog.type == 'drive';
                    let testSeqDoc =  bDriveTest ?
                        await this._driveTestSeqService.rxCollection.findOne(testLog.seqId).exec() :
                        await this.testSeqService.rxCollection.findOne(testLog.seqId).exec();

                    if ( testSeqDoc ) {
                        //console.log(`have testSeqDoc (importTime:${testSeqDoc.importTime},endTime:${testSeqDoc.endTime},status:${testSeqDoc.status}) for ${testLog.type} testLog`, testLog);
                        if ( testSeqDoc.importTime ) {
                            if ( testSeqDoc.importTime != testLog.importTime ) {
                                console.log(`RXDB has test log for ${testLog.pointId}.  Update import date from '${testLog.importTime}' to '${testSeqDoc.importTime}'`)
                                await this.dialerTestService.updateTestImportTime(testLog.seqId,testSeqDoc.importTime)
                            }
                        } else {
                            //console.log(`testSeqDoc has no importTime so not updating importTime on dialer box. status=${testSeqDoc.status}`)
                            // TBD the testSeqDoc.status field should be SERVER_ACCEPTED if in server db but just not yet imported
                            // If not yet SERVER_ACCEPTED but DIALER_SEQ_FINISHED, rxdb has not synced it yet
                        }
                    } else {
                        // rxdb doesn't have the testlog for some reason so add it
                        console.log(`RXDB is missing test log for ${testLog.pointId}.  Adding new testseq doc to be imported`)
                        const fullTestLog = await this.dialerTestService.getPiTestLogById(testLog.id);
                        if(bDriveTest) {
                            const doc = this._driveTestSeqService.createTestSeqDocFromPiDbTestLog(
                                fullTestLog,
                                TestSeqStatus.DIALER_SEQ_FINISHED
                            );
                            await doc.save();
                            console.log('syncUnimportedResultsFromPi() saved new DRIVE test', doc.toJSON())
                        } else {
                            const doc = this.testSeqService.createTestSeqDocFromPiDbTestLog(
                                fullTestLog,
                                TestSeqStatus.DIALER_SEQ_FINISHED
                            );
                            await doc.save();
                            console.log('syncUnimportedResultsFromPi() saved new PATH test', doc.toJSON())
                        }
                    }
                } catch ( error ) {
                    console.error(error)
                    this.appState.publishError(error)
                }
            }
        } catch ( error ) {
            console.error(error)
            const msg = `Dialer sync failed (${error})`;
            this.appState.publishErrorObj(error, msg, this, 30);
        }
    }

    get networkStatus$() {
        return this.dialerService.networkStatus$
    }


    createTimeStatusObservable = (maxWaitMs:number=5000) : Observable<TimeStatusResp> => this.dialerService.initialized$.pipe(
        skipWhile( bInitialized => !bInitialized ),
        //first(),
        switchMap(()=>
            race(
                this.httpClient.get( this.dialerService.config.url + 'app/now').pipe(
                    map( (timeInfo : any) => {
                        const nowSecsUtc = Date.now()/1000.0
                        const dialerSecsUtc = timeInfo.utc.timestamp
                        const diffInSeconds = Math.abs(dialerSecsUtc - nowSecsUtc)
                        let rtnTimeInfo : TimeStatusResp = {
                        ...timeInfo,
                        diffInSeconds
                        }
                        if ( diffInSeconds > 4 ) {
                        let msg = 'Time difference between dialer and webapp is '
                        msg += durationToText(diffInSeconds)
                        rtnTimeInfo.warningMsg = msg
                        }
                        return rtnTimeInfo
                    })
                ),
                timer(maxWaitMs).pipe(
                    map( _ => ({warningMsg: 'Time check max wait exceeded.'}))
                )
             )
        ),
        catchError( err => {
            console.error(err)
            return of({warningMsg:this.pathTestService.extractErrorMsg(err)})
         } )
    )

    getTestProfile( rootTest: RootTest ) : CallSetProfile&{earlyDisconnectCarrierRegex?: string; earlyDisconnectToleranceSecs?: number }|null {
        if ( !rootTest ) {
            return null;
        }
        // Looks like we will always have call tests in same format so just grab first voice call test and use that
        // to get the profile
        const flattenedTests: (GroupTest|VoiceCallTest)[] = this.dialerTestService.flattenTest(rootTest);
        console.log('getTestProfile() tests', flattenedTests)
        const someVoiceCallTest = flattenedTests.find( test => test.type === 'VoiceCallTest') as VoiceCallTest;
        console.log('getTestProfile() voice call test', someVoiceCallTest)
        if ( !someVoiceCallTest ) {
            return null;
        }
        const voiceCallParentGroupTest = flattenedTests.find( test => test.id === someVoiceCallTest.parentId) as GroupTest;
        const profile: CallSetProfile&{earlyDisconnectCarrierRegex?: string; earlyDisconnectToleranceSecs?: number } = {
            name: rootTest.name,
            callCount: voiceCallParentGroupTest.targetCnt,
            callDurationSecs: someVoiceCallTest.targetDurationSecs,
            callWaitSecs: voiceCallParentGroupTest.waitIntervalSecs,
            callSetupTimeoutSecs: someVoiceCallTest.setupTimeoutSecs,
            groupWaitSecs: rootTest.waitIntervalSecs,
            earlyDisconnectCarrierRegex: (someVoiceCallTest as any).earlyDisconnectCarrierRegex,
            earlyDisconnectToleranceSecs: (someVoiceCallTest as any).earlyDisconnectToleranceSecs,
        };
        return profile;
    }
}

export type TimeStatusResp = {
    warningMsg?: string
    diffInSeconds?: number
    utc?: {
        timestamp: number
    }

}